import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Trash2, PlayCircle, Shield, Palette, MapPin, X, Loader2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { BrandingConfigPanel } from "@/pages/BrandingConfigPanel";
import ExportCsvButton from "@/components/ExportCsvButton";
import MasterMailerHealthBanner from "@/components/master/MasterMailerHealthBanner";

const MODEL_OPTIONS = [
  { value: "gpt-4o", label: "gpt-4o", hint: "Default. Best balance of quality + cost." },
  { value: "gpt-4o-mini", label: "gpt-4o-mini", hint: "Cheapest. Fast. Good enough for most workspaces." },
  { value: "gpt-4-turbo", label: "gpt-4-turbo", hint: "Legacy premium. Slower + pricier than 4o." },
  { value: "gpt-5", label: "gpt-5", hint: "Highest quality. Only worth it for very verbose workspaces." },
];

export default function MasterTenantsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paceTenant, setPaceTenant] = useState(null); // { id, name, slug } when drawer open
  const [fedTenant, setFedTenant] = useState(null); // { id, name, slug } when federation drawer open
  const [brandTenant, setBrandTenant] = useState(null); // { id, name, slug } when branding drawer open
  const [geoTenant, setGeoTenant] = useState(null); // { id, name, slug } when geofence drawer open
  // Phase 29 · Item 1 — bulk-enable Pace selection state
  const [selected, setSelected] = useState(new Set());
  const [bulkPace, setBulkPace] = useState(false); // dialog open
  const [bulkKey, setBulkKey] = useState("");
  const [bulkModel, setBulkModel] = useState("gpt-4o");
  const [bulkSaving, setBulkSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/master/tenants");
      setItems(data.items);
      // Prune stale selections on refresh.
      setSelected((prev) => {
        const alive = new Set((data.items || []).map((t) => t.id));
        return new Set([...prev].filter((id) => alive.has(id)));
      });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  // Phase 29 · Item 1 — real workspaces only (never the master shell) are selectable.
  const selectable = items.filter((t) => t.slug !== "_master");
  const allSelected = selectable.length > 0 && selectable.every((t) => selected.has(t.id));
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(selectable.map((t) => t.id)));
  };
  const clearSelection = () => setSelected(new Set());

  const runBulkPace = async () => {
    if (!bulkKey.trim()) {
      toast.error("Paste an OpenAI key before enabling.");
      return;
    }
    setBulkSaving(true);
    try {
      const { data } = await api.post("/master/tenants/pace/bulk-enable", {
        tenant_ids: [...selected],
        openai_api_key: bulkKey.trim(),
        model: bulkModel,
      });
      const n = (data.enabled || []).length;
      const skipped = (data.skipped || []).length;
      const errors = (data.errors || []).length;
      if (n > 0) {
        toast.success(
          `Pace turned on for ${n} workspace${n === 1 ? "" : "s"}` +
          (skipped ? ` · ${skipped} skipped` : "") +
          (errors ? ` · ${errors} failed` : ""),
        );
      } else {
        toast.error(
          `Nothing enabled${skipped ? ` — ${skipped} skipped` : ""}` +
          `${errors ? ` — ${errors} failed` : ""}`,
        );
      }
      setBulkPace(false);
      setBulkKey("");
      clearSelection();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBulkSaving(false);
    }
  };

  const setStatus = async (t, status) => {
    try {
      await api.patch(`/master/tenants/${t.id}/status?status=${status}`);
      toast.success(`Workspace ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="master-tenants-page">
      <MasterMailerHealthBanner />

      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Platform admin</div>
        <h1 className="mt-1 font-display text-3xl font-semibold text-primary">Workspaces</h1>
        <p className="mt-2 text-muted-foreground">Everything across the platform. Only platform admins can see this page.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All workspaces</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[44px]">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={toggleAll}
                    aria-label="Select all workspaces"
                    data-testid="bulk-select-all"
                  />
                </TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Timezone</TableHead>
                <TableHead>Users</TableHead>
                <TableHead>Pace</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {items.map((t) => (
                <TableRow key={t.id} data-testid={`tenant-row-${t.slug}`}>
                  <TableCell>
                    {t.slug !== "_master" ? (
                      <Checkbox
                        checked={selected.has(t.id)}
                        onCheckedChange={() => toggleOne(t.id)}
                        aria-label={`Select ${t.name}`}
                        data-testid={`bulk-select-${t.slug}`}
                      />
                    ) : null}
                  </TableCell>
                  <TableCell className="font-medium">{t.name}</TableCell>
                  <TableCell className="text-muted-foreground">{t.slug}</TableCell>
                  <TableCell>{t.timezone}</TableCell>
                  <TableCell>{t.user_count}</TableCell>
                  <TableCell>
                    <Badge variant={t.pace_enabled ? "default" : "outline"} className="capitalize">
                      {t.pace_enabled ? "on" : "off"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={t.status === "active" ? "default" : "outline"} className="capitalize">
                      {t.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {t.slug !== "_master" && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => navigate(`/master/tenants/${t.id}`)}
                          data-testid={`view-detail-${t.slug}`}
                        >
                          Details
                        </Button>
                      )}
                      {t.slug !== "_master" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPaceTenant(t)}
                          data-testid={`configure-pace-${t.slug}`}
                        >
                          <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                          Pace
                        </Button>
                      )}
                      {t.slug !== "_master" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setFedTenant(t)}
                          data-testid={`configure-federation-${t.slug}`}
                        >
                          <Shield className="mr-1.5 h-3.5 w-3.5" />
                          Federation
                        </Button>
                      )}
                      {t.slug !== "_master" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setBrandTenant(t)}
                          data-testid={`configure-branding-${t.slug}`}
                        >
                          <Palette className="mr-1.5 h-3.5 w-3.5" />
                          Branding
                        </Button>
                      )}
                      {t.slug !== "_master" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setGeoTenant(t)}
                          data-testid={`view-geofence-${t.slug}`}
                        >
                          <MapPin className="mr-1.5 h-3.5 w-3.5" />
                          Geofence
                        </Button>
                      )}
                      {t.slug !== "_master" && (
                        <ExportCsvButton
                          endpoint={`master/tenants/${t.id}/timesheets/export.csv`}
                          filename={`quanta-${t.slug}-timesheets.csv`}
                          filters={{ status: "approved" }}
                          testId={`export-csv-master-${t.slug}`}
                          label="Export"
                        />
                      )}
                      {t.status === "active" ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setStatus(t, "suspended")}
                          data-testid={`suspend-${t.slug}`}
                        >
                          Suspend
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => setStatus(t, "active")}
                          data-testid={`activate-${t.slug}`}
                        >
                          Reactivate
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Sheet open={!!paceTenant} onOpenChange={(o) => !o && setPaceTenant(null)}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
          {paceTenant && (
            <PaceConfigPanel
              tenant={paceTenant}
              onClose={() => {
                setPaceTenant(null);
                load();
              }}
            />
          )}
        </SheetContent>
      </Sheet>

      <Sheet open={!!fedTenant} onOpenChange={(o) => !o && setFedTenant(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
          {fedTenant && (
            <FederationConfigPanel
              tenant={fedTenant}
              onClose={() => {
                setFedTenant(null);
                load();
              }}
            />
          )}
        </SheetContent>
      </Sheet>

      <Sheet open={!!brandTenant} onOpenChange={(o) => !o && setBrandTenant(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
          {brandTenant && (
            <BrandingConfigPanel
              tenant={brandTenant}
              onClose={() => {
                setBrandTenant(null);
                load();
              }}
            />
          )}
        </SheetContent>
      </Sheet>

      <Sheet open={!!geoTenant} onOpenChange={(o) => !o && setGeoTenant(null)}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
          {geoTenant && (
            <GeofenceReadOnlyPanel
              tenant={geoTenant}
              onClose={() => setGeoTenant(null)}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* Phase 29 · Item 1 — floating bulk-Pace action bar. */}
      {selected.size > 0 && (
        <div
          className="fixed inset-x-0 bottom-6 z-40 mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-card px-5 py-3 shadow-2xl ring-1 ring-secondary/20"
          data-testid="bulk-pace-bar"
        >
          <div className="flex items-center gap-3 text-sm">
            <Sparkles className="h-4 w-4 text-secondary" />
            <span className="font-medium text-primary">
              {selected.size} workspace{selected.size === 1 ? "" : "s"} selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={clearSelection}
              data-testid="bulk-pace-clear"
            >
              <X className="mr-1 h-3.5 w-3.5" /> Clear
            </Button>
            <Button
              size="sm"
              onClick={() => setBulkPace(true)}
              data-testid="bulk-pace-open"
            >
              <Sparkles className="mr-1 h-3.5 w-3.5" />
              Enable Pace on {selected.size} workspace{selected.size === 1 ? "" : "s"}
            </Button>
          </div>
        </div>
      )}

      <Dialog open={bulkPace} onOpenChange={(o) => !o && !bulkSaving && setBulkPace(false)}>
        <DialogContent className="max-w-lg" data-testid="bulk-pace-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-lg text-primary">
              Enable Pace on {selected.size} workspace{selected.size === 1 ? "" : "s"}?
            </DialogTitle>
            <DialogDescription>
              They'll share this OpenAI key and model. You can rotate the key per
              workspace later from the Pace drawer. Skipped workspaces (unknown IDs
              or the master shell) are reported in the response.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="bulk-pace-key">OpenAI API key</Label>
              <Input
                id="bulk-pace-key"
                type="password"
                autoComplete="off"
                value={bulkKey}
                onChange={(e) => setBulkKey(e.target.value)}
                placeholder="sk-..."
                data-testid="bulk-pace-key-input"
              />
              <p className="text-xs text-muted-foreground">
                Validated against OpenAI once before being encrypted at rest on
                each workspace. Never returned by any GET.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Model</Label>
              <Select value={bulkModel} onValueChange={setBulkModel}>
                <SelectTrigger data-testid="bulk-pace-model-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MODEL_OPTIONS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      <div className="flex flex-col">
                        <span className="font-medium">{m.label}</span>
                        <span className="text-xs text-muted-foreground">{m.hint}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBulkPace(false)}
              disabled={bulkSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={runBulkPace}
              disabled={bulkSaving || !bulkKey.trim()}
              data-testid="bulk-pace-confirm"
            >
              {bulkSaving ? (
                <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Enabling…</>
              ) : (
                <>Enable on {selected.size}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PaceConfigPanel({ tenant, onClose }) {
  const [state, setState] = useState(null);
  const [keyInput, setKeyInput] = useState("");
  const [model, setModel] = useState("gpt-4o");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const refresh = async () => {
    const { data } = await api.get(`/master/tenants/${tenant.id}/pace`);
    setState(data);
    setModel(data.model || "gpt-4o");
  };

  useEffect(() => {
    refresh();
    setKeyInput("");
    setTestResult(null);
  }, [tenant.id]);

  if (!state) {
    return (
      <div className="p-6 text-sm text-muted-foreground" data-testid="pace-config-loading">
        Loading…
      </div>
    );
  }

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (keyInput.trim()) payload.openai_api_key = keyInput.trim();
      if (model && model !== state.model) payload.model = model;
      if (Object.keys(payload).length === 0) {
        toast.info("Nothing to save");
        return;
      }
      await api.patch(`/master/tenants/${tenant.id}/pace`, payload);
      toast.success("Pace configuration updated");
      setKeyInput("");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (enabled) => {
    try {
      await api.patch(`/master/tenants/${tenant.id}/pace`, { enabled });
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post(`/master/tenants/${tenant.id}/pace/test`);
      setTestResult(data);
      if (data.ok) toast.success(`Test passed · ${data.latency_ms}ms`);
      else toast.error(`Test failed: ${data.error || "unknown"}`);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTesting(false);
    }
  };

  const clearKey = async () => {
    if (!confirm(`Clear Pace key for ${tenant.name}? This disables Pace for the tenant.`)) return;
    try {
      await api.delete(`/master/tenants/${tenant.id}/pace/key`);
      toast.success("Key cleared");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="flex h-full flex-col gap-6" data-testid="pace-config-panel">
      <SheetHeader className="text-left">
        <div className="flex items-center gap-3">
          <img
            src="/brand/pace/pace-avatar-128.png"
            alt=""
            className="h-10 w-10 rounded-full ring-1 ring-secondary/40"
          />
          <div>
            <SheetTitle className="font-display text-xl text-primary">
              Pace · {tenant.name}
            </SheetTitle>
            <SheetDescription className="text-xs uppercase tracking-widest text-muted-foreground">
              Per-tenant OpenAI configuration · master-only
            </SheetDescription>
          </div>
        </div>
      </SheetHeader>

      <div className="rounded-xl border border-border bg-accent/40 p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Enabled</div>
            <div className="mt-1 font-display text-lg font-semibold text-primary">
              {state.enabled ? "On" : "Off"}
            </div>
            {state.prefix ? (
              <div className="mt-1 font-mono text-xs text-muted-foreground" data-testid="pace-key-prefix">
                {state.prefix}
                <span aria-hidden>•••••••</span>
              </div>
            ) : (
              <div className="mt-1 text-xs text-muted-foreground">No key on file</div>
            )}
          </div>
          <Switch
            checked={state.enabled}
            onCheckedChange={toggle}
            disabled={!state.has_key}
            data-testid="pace-enable-toggle"
            aria-label="Toggle Pace"
          />
        </div>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="pace-key-input" className="text-sm font-medium text-primary">
            {state.has_key ? "Rotate key" : "OpenAI API key"}
          </Label>
          <Input
            id="pace-key-input"
            type="password"
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            placeholder="sk-..."
            autoComplete="off"
            spellCheck={false}
            data-testid="pace-key-input"
          />
          <p className="text-xs text-muted-foreground">
            Encrypted at rest with the platform MFA key. Never returned by any GET.
            {" "}
            <a
              href="https://platform.openai.com/api-keys"
              target="_blank"
              rel="noopener noreferrer"
              className="text-secondary underline decoration-dotted underline-offset-2"
            >
              Where do I get one?
            </a>
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="pace-model" className="text-sm font-medium text-primary">
            Model
          </Label>
          <Select value={model} onValueChange={setModel}>
            <SelectTrigger id="pace-model" data-testid="pace-model-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MODEL_OPTIONS.map((m) => (
                <SelectItem key={m.value} value={m.value}>
                  <div className="flex flex-col">
                    <span className="font-medium">{m.label}</span>
                    <span className="text-xs text-muted-foreground">{m.hint}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <Button onClick={save} disabled={saving} data-testid="pace-save-button">
            {saving ? "Saving…" : state.has_key ? "Rotate & save" : "Save & enable"}
          </Button>
          <Button
            variant="outline"
            onClick={runTest}
            disabled={!state.has_key || testing}
            data-testid="pace-test-button"
          >
            <PlayCircle className="mr-1.5 h-4 w-4" />
            {testing ? "Testing…" : "Test Pace"}
          </Button>
          {state.has_key && (
            <Button
              variant="ghost"
              onClick={clearKey}
              className="text-danger hover:bg-danger/10 hover:text-danger"
              data-testid="pace-clear-button"
            >
              <Trash2 className="mr-1.5 h-4 w-4" />
              Clear key
            </Button>
          )}
        </div>
      </div>

      <div className="mt-auto space-y-2 rounded-xl border border-dashed border-border p-4 text-xs text-muted-foreground">
        <div>
          Last test:{" "}
          <span className="font-mono text-primary">
            {state.last_test_at || "never"}
          </span>
        </div>
        {testResult && (
          <div data-testid="pace-test-result">
            Result:{" "}
            <span className={testResult.ok ? "font-medium text-success" : "font-medium text-danger"}>
              {testResult.ok ? `ok · ${testResult.latency_ms}ms` : `failed · ${testResult.error}`}
            </span>
          </div>
        )}
        {state.last_error && !testResult && (
          <div className="text-danger">Last error: {state.last_error}</div>
        )}
      </div>

      <div className="flex justify-end">
        <Button variant="outline" onClick={onClose} data-testid="pace-close-button">
          Close
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase 11b — Federation configuration panel (master-only)
// ---------------------------------------------------------------------------

const AUTH_MODE_OPTIONS = [
  { value: "native", label: "Native", hint: "Email/password only. Federation endpoints return 400." },
  { value: "federated", label: "Federated", hint: "Federation only. Workspace users cannot email/password log in." },
  { value: "hybrid", label: "Hybrid", hint: "Both flows accepted. Federation is opt-in per user." },
];

const ALGO_OPTIONS = [
  { value: "HS256", label: "HS256 (shared secret)" },
  { value: "RS256", label: "RS256 (public key)" },
];

const DEFAULT_CLAIM_MAP = {
  subject: "sub",
  email: "email",
  full_name: "name",
  roles: "quanta.roles",
  external_id: "sub",
  worksite_codes: "quanta.worksite_codes",
  employee_id: "quanta.employee_id",
  timezone: "zoneinfo",
};

function ChipInput({ id, values, onChange, placeholder, testid }) {
  const [draft, setDraft] = useState("");
  const commit = () => {
    const v = draft.trim();
    if (!v) return;
    if (!values.includes(v)) onChange([...values, v]);
    setDraft("");
  };
  return (
    <div className="rounded-md border border-input bg-background px-3 py-2" data-testid={testid}>
      <div className="flex flex-wrap gap-1.5">
        {values.map((v) => (
          <Badge key={v} variant="secondary" className="gap-1.5 font-mono text-[11px]">
            {v}
            <button
              type="button"
              onClick={() => onChange(values.filter((x) => x !== v))}
              className="text-muted-foreground transition hover:text-danger"
              aria-label={`Remove ${v}`}
              data-testid={`${testid}-remove-${v}`}
            >
              ×
            </button>
          </Badge>
        ))}
        <input
          id={id}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              commit();
            } else if (e.key === "Backspace" && !draft && values.length) {
              onChange(values.slice(0, -1));
            }
          }}
          onBlur={commit}
          placeholder={placeholder}
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground min-w-[8ch]"
        />
      </div>
    </div>
  );
}

function FederationConfigPanel({ tenant, onClose }) {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testAssertion, setTestAssertion] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const [secretDraft, setSecretDraft] = useState("");
  const [pubKeyDraft, setPubKeyDraft] = useState("");

  const refresh = async () => {
    const { data } = await api.get(`/master/tenants/${tenant.id}/federation`);
    setState(data);
    setSecretDraft("");
    setPubKeyDraft(data.federation?.rs256_public_key_pem || "");
  };

  useEffect(() => {
    refresh();
    setTestResult(null);
    setShowSecret(false);
  }, [tenant.id]);

  if (!state) {
    return (
      <div className="p-6 text-sm text-muted-foreground" data-testid="federation-config-loading">
        Loading…
      </div>
    );
  }

  const fed = state.federation || {};
  const claimMap = { ...DEFAULT_CLAIM_MAP, ...(fed.claim_map || {}) };

  const patch = async (body) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/master/tenants/${tenant.id}/federation`, body);
      setState(data);
      toast.success("Federation updated");
      setSecretDraft("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const setAuthMode = (auth_mode) => patch({ auth_mode });
  const toggleEnabled = (enabled) => patch({ federation: { enabled } });
  const saveIssuer = (issuer) => patch({ federation: { issuer } });
  const saveAlgo = (algorithm) => patch({ federation: { algorithm } });
  const saveDefaultRole = (default_role) => patch({ federation: { default_role } });
  const saveAllowFlags = (patchObj) => patch({ federation: patchObj });
  const saveClaimMap = (nextMap) => patch({ federation: { claim_map: nextMap } });
  const saveDomains = (allowed_email_domains) => patch({ federation: { allowed_email_domains } });
  const saveOrigins = (allowed_embed_origins) => patch({ federation: { allowed_embed_origins } });
  const saveSecret = () => {
    if (!secretDraft.trim()) {
      toast.info("Paste a secret to rotate");
      return;
    }
    patch({ federation: { hs256_secret: secretDraft.trim() } });
  };
  const savePubKey = () => {
    if (!pubKeyDraft.trim() || pubKeyDraft === fed.rs256_public_key_pem) {
      toast.info("Nothing to save");
      return;
    }
    patch({ federation: { rs256_public_key_pem: pubKeyDraft.trim() } });
  };

  const clearMaterial = async (kind) => {
    if (!confirm(`Clear ${kind.toUpperCase()} material for ${tenant.name}? This disables federation.`)) return;
    try {
      const { data } = await api.delete(`/master/tenants/${tenant.id}/federation/secret?kind=${kind}`);
      setState(data);
      toast.success(`${kind.toUpperCase()} cleared`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const runTest = async () => {
    if (!testAssertion.trim()) return;
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post(
        `/master/tenants/${tenant.id}/federation/test`,
        { assertion: testAssertion.trim() },
      );
      setTestResult(data);
      if (data.ok) toast.success(`Test passed · ${data.latency_ms}ms`);
      else toast.error(`Test failed: ${data.error || "unknown"}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="flex h-full flex-col gap-6" data-testid="federation-config-panel">
      <SheetHeader className="text-left">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-full bg-secondary/10 text-secondary ring-1 ring-secondary/30">
            <Shield className="h-5 w-5" />
          </div>
          <div>
            <SheetTitle className="font-display text-xl text-primary">
              Federation · {tenant.name}
            </SheetTitle>
            <SheetDescription className="text-xs uppercase tracking-widest text-muted-foreground">
              Partner-signed SSO · master-only
            </SheetDescription>
          </div>
        </div>
      </SheetHeader>

      {/* Auth mode */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-primary">Auth mode</Label>
        <Select value={state.auth_mode} onValueChange={setAuthMode} disabled={saving}>
          <SelectTrigger data-testid="federation-auth-mode-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {AUTH_MODE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                <div className="flex flex-col">
                  <span className="font-medium">{o.label}</span>
                  <span className="text-xs text-muted-foreground">{o.hint}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Separator />

      {/* Enabled switch */}
      <div className="rounded-xl border border-border bg-accent/40 p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Enabled</div>
            <div className="mt-1 font-display text-lg font-semibold text-primary">
              {fed.enabled ? "On" : "Off"}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              Algorithm: <span className="font-mono">{fed.algorithm || "HS256"}</span>
            </div>
          </div>
          <Switch
            checked={!!fed.enabled}
            onCheckedChange={toggleEnabled}
            disabled={saving}
            data-testid="federation-enable-toggle"
            aria-label="Toggle federation"
          />
        </div>
      </div>

      {/* Issuer + algorithm */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="fed-issuer" className="text-sm font-medium text-primary">
            Issuer (`iss`)
          </Label>
          <Input
            id="fed-issuer"
            defaultValue={fed.issuer || ""}
            placeholder="https://idp.partner.com"
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v && v !== fed.issuer) saveIssuer(v);
            }}
            data-testid="federation-issuer-input"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-medium text-primary">Algorithm</Label>
          <Select value={fed.algorithm || "HS256"} onValueChange={saveAlgo} disabled={saving}>
            <SelectTrigger data-testid="federation-algorithm-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ALGO_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Secret / public key */}
      {(fed.algorithm || "HS256") === "HS256" ? (
        <div className="space-y-2">
          <Label htmlFor="fed-hs256" className="text-sm font-medium text-primary">
            HS256 shared secret
          </Label>
          <div className="flex items-center gap-2">
            <Input
              id="fed-hs256"
              type={showSecret ? "text" : "password"}
              value={secretDraft}
              onChange={(e) => setSecretDraft(e.target.value)}
              placeholder={fed.has_hs256_secret ? "Rotate secret…" : "Paste new secret"}
              autoComplete="off"
              data-testid="federation-hs256-input"
            />
            <Button variant="outline" size="sm" type="button" onClick={() => setShowSecret((v) => !v)}>
              {showSecret ? "Hide" : "Show"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Stored Fernet-encrypted with the platform MFA key.
            {fed.has_hs256_secret ? " Never returned by any GET." : " No secret on file yet."}
          </p>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              onClick={saveSecret}
              disabled={saving || !secretDraft.trim()}
              data-testid="federation-hs256-save"
            >
              {fed.has_hs256_secret ? "Rotate secret" : "Save secret"}
            </Button>
            {fed.has_hs256_secret && (
              <Button
                variant="ghost"
                onClick={() => clearMaterial("hs256")}
                className="text-danger hover:bg-danger/10 hover:text-danger"
                data-testid="federation-hs256-clear"
              >
                <Trash2 className="mr-1.5 h-4 w-4" /> Clear
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <Label htmlFor="fed-rs256" className="text-sm font-medium text-primary">
            RS256 public key (PEM)
          </Label>
          <Textarea
            id="fed-rs256"
            value={pubKeyDraft}
            onChange={(e) => setPubKeyDraft(e.target.value)}
            rows={6}
            className="font-mono text-xs"
            placeholder="-----BEGIN PUBLIC KEY-----"
            data-testid="federation-rs256-input"
          />
          <div className="flex flex-wrap gap-2 pt-1">
            <Button onClick={savePubKey} disabled={saving} data-testid="federation-rs256-save">
              Save public key
            </Button>
            {fed.rs256_public_key_pem && (
              <Button
                variant="ghost"
                onClick={() => clearMaterial("rs256")}
                className="text-danger hover:bg-danger/10 hover:text-danger"
                data-testid="federation-rs256-clear"
              >
                <Trash2 className="mr-1.5 h-4 w-4" /> Clear
              </Button>
            )}
          </div>
        </div>
      )}

      <Separator />

      {/* Claim map */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-primary">Claim map</Label>
        <p className="text-xs text-muted-foreground">
          Dotted-path lookup into the JWT. Blank = use the default in parentheses.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {Object.entries(DEFAULT_CLAIM_MAP).map(([key, def]) => (
            <div key={key} className="space-y-1">
              <Label htmlFor={`cm-${key}`} className="text-[11px] uppercase tracking-widest text-muted-foreground">
                {key} <span className="normal-case text-muted-foreground/70">({def})</span>
              </Label>
              <Input
                id={`cm-${key}`}
                defaultValue={claimMap[key] || ""}
                placeholder={def}
                onBlur={(e) => {
                  const v = e.target.value.trim() || def;
                  if (v !== claimMap[key]) saveClaimMap({ ...claimMap, [key]: v });
                }}
                data-testid={`federation-claim-${key}`}
              />
            </div>
          ))}
        </div>
      </div>

      <Separator />

      {/* Policy toggles */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label className="text-sm font-medium text-primary">Default role (new users)</Label>
          <Select value={fed.default_role || "worker"} onValueChange={saveDefaultRole} disabled={saving}>
            <SelectTrigger data-testid="federation-default-role-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {["worker", "approver", "admin", "tenant_admin"].map((r) => (
                <SelectItem key={r} value={r}>{r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col justify-end gap-3 rounded-md border border-dashed border-border p-3">
          <label className="flex items-center justify-between gap-3 text-sm">
            <span className="font-medium text-primary">Auto-provision new users</span>
            <Switch
              checked={!!fed.allow_auto_provision}
              onCheckedChange={(v) => saveAllowFlags({ allow_auto_provision: v })}
              data-testid="federation-auto-provision-toggle"
            />
          </label>
          <label className="flex items-center justify-between gap-3 text-sm">
            <span className="font-medium text-primary">Allow external_id relink</span>
            <Switch
              checked={!!fed.allow_relink}
              onCheckedChange={(v) => saveAllowFlags({ allow_relink: v })}
              data-testid="federation-allow-relink-toggle"
            />
          </label>
          <label className="flex items-start justify-between gap-3 text-sm">
            <span className="flex flex-col gap-0.5">
              <span className="font-medium text-primary">Use roles from verified sign-in assertions</span>
              <span className="text-xs text-muted-foreground">
                When on, each verified sign-in overwrites this tenant's membership roles from the assertion. Off by default.
              </span>
            </span>
            <Switch
              checked={!!fed.sync_roles_from_assertion}
              onCheckedChange={(v) => saveAllowFlags({ sync_roles_from_assertion: v })}
              data-testid="federation-sync-roles-toggle"
            />
          </label>
        </div>
      </div>

      {/* Domain + Origin chips */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-primary">Allowed email domains</Label>
        <ChipInput
          values={fed.allowed_email_domains || []}
          onChange={saveDomains}
          placeholder="acme.co, partner.com"
          testid="federation-domains"
        />
        <p className="text-xs text-muted-foreground">
          Empty = accept any domain. When set, mismatched emails return 403.
        </p>
      </div>
      <div className="space-y-2">
        <Label className="text-sm font-medium text-primary">Allowed embed origins</Label>
        <ChipInput
          values={fed.allowed_embed_origins || []}
          onChange={saveOrigins}
          placeholder="https://partner.example.com"
          testid="federation-origins"
        />
        <p className="text-xs text-muted-foreground">
          Requests from these origins get the `quanta_access` httpOnly cookie set. Bearer flow otherwise.
        </p>
      </div>

      <Separator />

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={() => setTestOpen(true)}
          disabled={!fed.enabled}
          data-testid="federation-test-open"
        >
          <PlayCircle className="mr-1.5 h-4 w-4" />
          Test assertion
        </Button>
      </div>

      <div className="mt-auto space-y-1 rounded-xl border border-dashed border-border p-4 text-xs text-muted-foreground">
        <div>
          Last test:{" "}
          <span className="font-mono text-primary">{fed.last_test_at || "never"}</span>
        </div>
        {fed.last_error && (
          <div className="text-danger">Last error: {fed.last_error}</div>
        )}
      </div>

      {/* Phase 17a — SAML 2.0 subsection */}
      <SamlSubsection tenantId={tenant.id} tenantSlug={tenant.slug} tenantName={tenant.name} />

      <div className="flex justify-end">
        <Button variant="outline" onClick={onClose} data-testid="federation-close-button">
          Close
        </Button>
      </div>

      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="max-w-lg" data-testid="federation-test-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-lg text-primary">Test a partner assertion</DialogTitle>
            <DialogDescription>
              Verifies the JWT against the stored config. Never creates a user or issues a Quanta token.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={testAssertion}
            onChange={(e) => setTestAssertion(e.target.value)}
            rows={6}
            placeholder="eyJhbGciOiJIUzI1NiIs…"
            className="font-mono text-xs"
            data-testid="federation-test-input"
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setTestOpen(false)}>Cancel</Button>
            <Button onClick={runTest} disabled={testing || !testAssertion.trim()} data-testid="federation-test-run">
              {testing ? "Testing…" : "Run test"}
            </Button>
          </div>
          {testResult && (
            <div className="rounded-md border border-border p-3 text-sm" data-testid="federation-test-result">
              {testResult.ok ? (
                <div className="space-y-1">
                  <div className="font-medium text-success">
                    ok · {testResult.latency_ms}ms
                  </div>
                  <pre className="max-h-64 overflow-auto rounded bg-muted/50 p-2 text-[11px]">
                    {JSON.stringify(testResult.extracted_claims, null, 2)}
                  </pre>
                </div>
              ) : (
                <div className="font-medium text-danger">
                  failed · {testResult.error}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Phase 17a — SAML 2.0 configuration subsection.
// Mounted inside the FederationConfigPanel sheet; owns its own state so
// it can PATCH the SAML sub-doc without touching the JWT-federation surface.
// ---------------------------------------------------------------------------
function SamlSubsection({ tenantId, tenantSlug, tenantName }) {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);
  const [certDraft, setCertDraft] = useState("");
  const [testOpen, setTestOpen] = useState(false);
  const [testResp, setTestResp] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/master/tenants/${tenantId}/federation/saml`);
      setState(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [tenantId]);

  if (!state) {
    return (
      <div className="rounded-xl border border-dashed p-4 text-xs text-muted-foreground" data-testid="saml-config-loading">
        Loading SAML config&hellip;
      </div>
    );
  }

  const patch = async (body) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/master/tenants/${tenantId}/federation/saml`, body);
      setState(data);
      toast.success("SAML config updated");
      setCertDraft("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const downloadMetadata = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/v1/auth/saml/metadata/${tenantSlug}`;
    window.open(url, "_blank");
  };

  const runTest = async () => {
    if (!testResp.trim()) return;
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post(
        `/master/tenants/${tenantId}/federation/saml/test`,
        { saml_response: testResp.trim() },
      );
      setTestResult(data);
      if (data.ok) toast.success("SAMLResponse verified");
      else toast.error(`Test failed: ${data.error}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTesting(false);
    }
  };

  const clearAll = async () => {
    if (!confirm(`Disable SAML + clear cert for ${tenantName}?`)) return;
    try {
      const { data } = await api.delete(`/master/tenants/${tenantId}/federation/saml`);
      setState(data);
      toast.success("SAML disabled");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-4 rounded-xl border border-secondary/40 bg-secondary/5 p-4" data-testid="saml-config-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-secondary">
            SAML 2.0
          </div>
          <div className="font-display text-lg font-semibold text-primary">
            Partner IdP SSO
          </div>
          <div className="text-xs text-muted-foreground">
            SP entity: <code className="text-primary">{state.sp_entity_id}</code>
          </div>
        </div>
        <Switch
          checked={!!state.enabled}
          onCheckedChange={(enabled) => patch({ enabled })}
          disabled={saving}
          data-testid="saml-enable-toggle"
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="saml-idp-entity" className="text-xs">IdP entity ID</Label>
          <Input
            id="saml-idp-entity"
            defaultValue={state.idp_entity_id || ""}
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v !== state.idp_entity_id) patch({ idp_entity_id: v });
            }}
            data-testid="saml-idp-entity-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="saml-idp-sso" className="text-xs">IdP SSO URL</Label>
          <Input
            id="saml-idp-sso"
            defaultValue={state.idp_sso_url || ""}
            placeholder="https://idp.partner.com/sso"
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v !== state.idp_sso_url) patch({ idp_sso_url: v });
            }}
            data-testid="saml-idp-sso-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="saml-idp-slo" className="text-xs">IdP SLO URL (optional)</Label>
          <Input
            id="saml-idp-slo"
            defaultValue={state.idp_slo_url || ""}
            placeholder="https://idp.partner.com/slo"
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v !== state.idp_slo_url) patch({ idp_slo_url: v });
            }}
            data-testid="saml-idp-slo-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="saml-nameid" className="text-xs">NameID format</Label>
          <Select
            value={state.nameid_format}
            onValueChange={(v) => patch({ nameid_format: v })}
          >
            <SelectTrigger id="saml-nameid" data-testid="saml-nameid-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress">
                emailAddress
              </SelectItem>
              <SelectItem value="urn:oasis:names:tc:SAML:2.0:nameid-format:persistent">
                persistent
              </SelectItem>
              <SelectItem value="urn:oasis:names:tc:SAML:2.0:nameid-format:transient">
                transient
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="saml-cert" className="text-xs">
          IdP X.509 signing certificate (PEM)
          {state.has_cert && state.cert_not_after && (
            <span className="ml-2 text-[10px] text-muted-foreground">
              expires {state.cert_not_after.slice(0, 10)}
            </span>
          )}
        </Label>
        <Textarea
          id="saml-cert"
          value={certDraft}
          onChange={(e) => setCertDraft(e.target.value)}
          rows={4}
          placeholder="-----BEGIN CERTIFICATE-----&#10;..."
          className="font-mono text-[10px]"
          data-testid="saml-cert-textarea"
        />
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => patch({ idp_x509_cert_pem: certDraft })}
            disabled={saving || !certDraft.trim()}
            data-testid="saml-cert-save"
          >
            Save cert
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-xs">Default role</Label>
          <Select
            value={state.default_role}
            onValueChange={(v) => patch({ default_role: v })}
          >
            <SelectTrigger data-testid="saml-default-role-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="worker">worker</SelectItem>
              <SelectItem value="approver">approver</SelectItem>
              <SelectItem value="admin">admin</SelectItem>
              <SelectItem value="tenant_admin">tenant_admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-2 pt-4">
          <label className="flex items-center gap-2 text-xs" data-testid="saml-auto-provision-label">
            <Switch
              checked={!!state.allow_auto_provision}
              onCheckedChange={(v) => patch({ allow_auto_provision: v })}
              data-testid="saml-auto-provision-toggle"
            />
            Auto-provision
          </label>
          <label className="flex items-center gap-2 text-xs" data-testid="saml-allow-relink-label">
            <Switch
              checked={!!state.allow_relink}
              onCheckedChange={(v) => patch({ allow_relink: v })}
              data-testid="saml-allow-relink-toggle"
            />
            Allow relink
          </label>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={downloadMetadata} data-testid="saml-download-metadata">
          Download SP metadata
        </Button>
        <Button size="sm" variant="outline" onClick={() => setTestOpen(true)} data-testid="saml-test-btn">
          Test SAMLResponse
        </Button>
        <Button size="sm" variant="destructive" onClick={clearAll} data-testid="saml-clear-btn">
          Disable + clear cert
        </Button>
      </div>

      <div className="rounded-lg border border-dashed p-3 text-[11px] text-muted-foreground">
        <div>Last test: <span className="font-mono text-primary">{state.last_test_at || "never"}</span></div>
        {state.last_error && <div className="text-danger">Last error: {state.last_error}</div>}
      </div>

      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="max-w-lg" data-testid="saml-test-modal">
          <DialogHeader>
            <DialogTitle>Test a SAMLResponse</DialogTitle>
            <DialogDescription>
              Paste a base64-encoded SAMLResponse. We verify it against the stored config and return
              extracted claims. No user is created and no Quanta token is issued.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={testResp}
            onChange={(e) => setTestResp(e.target.value)}
            rows={6}
            placeholder="PHNhbWxwOlJlc3BvbnNlIC4uLg=="
            className="font-mono text-xs"
            data-testid="saml-test-input"
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setTestOpen(false)}>Cancel</Button>
            <Button onClick={runTest} disabled={testing || !testResp.trim()} data-testid="saml-test-run">
              {testing ? "Testing…" : "Run test"}
            </Button>
          </div>
          {testResult && (
            <div className="rounded-md border p-3 text-sm" data-testid="saml-test-result">
              {testResult.ok ? (
                <>
                  <div className="font-medium text-success">Verified</div>
                  <pre className="mt-1 max-h-64 overflow-auto rounded bg-muted/50 p-2 text-[11px]">
                    {JSON.stringify(testResult.extracted_claims, null, 2)}
                  </pre>
                </>
              ) : (
                <div className="font-medium text-danger">failed &middot; {testResult.error}</div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}



// ---------------------------------------------------------------------------
// Phase 20 — Master read-only Geofence card.
// Surface the tenant's rollup: consent status, per-worksite fence settings,
// and worker-acceptance counters. No writes — configuration happens in the
// tenant admin console.
// ---------------------------------------------------------------------------
function GeofenceReadOnlyPanel({ tenant, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setError(null);
    setData(null);
    api.get(`/master/tenants/${tenant.id}/geofence`)
      .then((r) => setData(r.data))
      .catch((e) => setError(formatApiError(e)));
  }, [tenant.id]);

  if (error) {
    return (
      <div className="p-6 text-sm text-danger" data-testid="geofence-view-error">
        {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="p-6 text-sm text-muted-foreground" data-testid="geofence-view-loading">
        Loading…
      </div>
    );
  }

  const cfg = data.geofence_consent || {};
  const stats = data.stats || {};
  return (
    <div className="flex h-full flex-col gap-6" data-testid="geofence-view-panel">
      <SheetHeader className="text-left">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-full bg-secondary/10 text-secondary ring-1 ring-secondary/30">
            <MapPin className="h-5 w-5" />
          </div>
          <div>
            <SheetTitle className="font-display text-xl text-primary">
              Geofence · {tenant.name}
            </SheetTitle>
            <SheetDescription className="text-xs uppercase tracking-widest text-muted-foreground">
              Read-only tenant rollup · master console
            </SheetDescription>
          </div>
        </div>
      </SheetHeader>

      <div className="rounded-xl border border-border bg-accent/40 p-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Privacy notice</div>
            <div className="mt-1 font-display text-lg font-semibold text-primary" data-testid="geofence-view-enabled">
              {cfg.enabled ? "Enabled" : "Disabled"}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              Current version: <span className="font-mono text-primary" data-testid="geofence-view-version">v{cfg.privacy_notice_version || 0}</span>
            </div>
          </div>
          <Badge variant={cfg.enabled ? "default" : "outline"} className="capitalize">
            {cfg.enabled ? "consent required" : "no consent"}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <StatBox
          label="Worksites total"
          value={stats.worksites_total || 0}
          testid="geofence-stats-worksites-total"
        />
        <StatBox
          label="With geofence"
          value={stats.worksites_with_geofence || 0}
          testid="geofence-stats-worksites-with-geo"
        />
        <StatBox
          label="Active workers"
          value={stats.workers_active || 0}
          testid="geofence-stats-workers-active"
        />
        <StatBox
          label="Consented (current v)"
          value={stats.workers_consented_current_version || 0}
          testid="geofence-stats-workers-consented"
        />
      </div>

      <div className="space-y-2">
        <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
          Worksites ({(data.worksites || []).length})
        </div>
        <div className="space-y-2" data-testid="geofence-view-worksites">
          {(data.worksites || []).map((w) => (
            <div
              key={w.id}
              className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-2 text-sm"
              data-testid={`geofence-view-worksite-${w.code}`}
            >
              <div>
                <div className="font-medium text-primary">{w.name}</div>
                <div className="text-xs text-muted-foreground">
                  <span className="font-mono">{w.code}</span>
                  {" · "}
                  {w.active ? "active" : "inactive"}
                </div>
              </div>
              <div className="text-right text-xs">
                {w.geo && w.geo.enabled ? (
                  <>
                    <Badge variant="default" className="mb-1">geofence on</Badge>
                    <div className="text-muted-foreground">
                      {w.geo.radius_meters}m ·{" "}
                      <span className="font-mono">{Number(w.geo.lat).toFixed(4)}, {Number(w.geo.lng).toFixed(4)}</span>
                    </div>
                    <div className="text-muted-foreground">
                      auto {w.geo.auto_confirm_after_hours}h · {w.geo.enforce_manual_clock_in}
                    </div>
                  </>
                ) : (
                  <Badge variant="outline">no geofence</Badge>
                )}
              </div>
            </div>
          ))}
          {(data.worksites || []).length === 0 && (
            <div className="rounded-lg border border-dashed border-border py-6 text-center text-sm text-muted-foreground">
              No worksites yet.
            </div>
          )}
        </div>
      </div>

      <div className="mt-auto flex justify-end">
        <Button variant="outline" onClick={onClose} data-testid="geofence-view-close-btn">
          Close
        </Button>
      </div>
    </div>
  );
}

function StatBox({ label, value, testid }) {
  return (
    <div className="rounded-lg border border-border bg-background p-3" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-2xl font-semibold text-primary">{value}</div>
    </div>
  );
}
